import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { rideService } from '../../../../services/rideService';
import Icon from '../../../../components/AppIcon';

const RideCard = ({ ride, currentUserId, onAction, eventId }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDriver = ride?.driver_id === currentUserId;
  const isPassenger = ride?.passengers?.some(p => p?.passenger_id === currentUserId && p?.status === 'approved');
  
  // Fix: Calculate occupied seats from passengers array
  const occupied = ride?.passengers ? ride?.passengers?.length : 0;
  const availableSeats = ride?.available_seats - occupied;
  const isFull = availableSeats <= 0;

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Belirtilmemiş';
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  const handleJoinRide = async () => {
    if (!currentUserId) {
      alert('Araca katılmak için giriş yapmalısınız');
      return;
    }

    if (isFull) {
      alert('Bu araçta boş koltuk kalmamıştır');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await rideService?.joinRide(ride?.id, eventId);
      onAction?.();
    } catch (err) {
      console.error('Error joining ride:', err);
      setError(err?.message || 'Araca katılırken hata oluştu');
      alert(err?.message || 'Araca katılırken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeaveRide = async () => {
    if (!confirm('Bu araçtan ayrılmak istediğinize emin misiniz?')) return;

    try {
      setIsLoading(true);
      setError(null);
      await rideService?.leaveRide(ride?.id, eventId);
      onAction?.();
    } catch (err) {
      console.error('Error leaving ride:', err);
      setError(err?.message || 'Araçtan ayrılırken hata oluştu');
      alert(err?.message || 'Araçtan ayrılırken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelRide = async () => {
    if (!confirm('İlanı iptal etmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) return;

    try {
      setIsLoading(true);
      setError(null);
      await rideService?.cancelRide(ride?.id);
      onAction?.();
    } catch (err) {
      console.error('Error canceling ride:', err);
      setError(err?.message || 'İlan iptal edilirken hata oluştu');
      alert(err?.message || 'İlan iptal edilirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartChat = (targetUserId) => {
    navigate('/messaging-center', { state: { startChatWith: targetUserId } });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
      {/* Driver Info */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white font-semibold text-lg overflow-hidden">
          {ride?.driver?.avatar_url ? (
            <img
              src={ride?.driver?.avatar_url}
              alt={ride?.driver?.full_name || 'Sürücü'}
              className="w-full h-full object-cover"
            />
          ) : (
            <span>{ride?.driver?.full_name?.charAt(0)?.toUpperCase() || 'S'}</span>
          )}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-foreground">{ride?.driver?.full_name || 'İsimsiz Sürücü'}</p>
          <p className="text-xs text-muted-foreground">Sürücü</p>
        </div>
        <div className="flex items-center gap-2">
          {isDriver && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              Sizin İlanınız
            </span>
          )}
          {/* Message button for passengers to contact driver */}
          {isPassenger && !isDriver && (
            <button
              onClick={() => handleStartChat(ride?.driver_id)}
              className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              title="Sürücüye Mesaj Gönder"
            >
              <Icon name="MessageCircle" size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Route Info */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Icon name="MapPin" size={16} className="text-primary flex-shrink-0" />
          <span className="font-medium text-foreground truncate">{ride?.origin_location || 'Başlangıç noktası'}</span>
        </div>
        <div className="flex items-center gap-2 my-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <Icon name="ArrowDown" size={14} className="text-muted-foreground" />
          </div>
          <span className="text-xs text-muted-foreground">Etkinlik Konumu</span>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Time */}
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={16} className="text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Kalkış</p>
            <p className="text-sm font-medium text-foreground">{formatTime(ride?.departure_time)}</p>
          </div>
        </div>

        {/* Price */}
        <div className="flex items-center gap-2">
          <Icon name="Wallet" size={16} className="text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Ücret</p>
            <p className="text-sm font-medium text-foreground">
              {ride?.price_per_seat > 0 ? `${ride?.price_per_seat} TL` : 'Ücretsiz'}
            </p>
          </div>
        </div>
      </div>

      {/* Seats Badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Users" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Koltuk Durumu:</span>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          isFull 
            ? 'bg-red-100 text-red-700' 
            : availableSeats <= 1 
            ? 'bg-orange-100 text-orange-700' :'bg-green-100 text-green-700'
        }`}>
          {occupied}/{ride?.available_seats} Dolu
        </span>
      </div>

      {/* Car Details & Notes */}
      {(ride?.car_details || ride?.notes) && (
        <div className="border-t border-gray-200 pt-3 mb-4 space-y-2">
          {ride?.car_details && (
            <div className="flex items-start gap-2">
              <Icon name="Car" size={14} className="text-muted-foreground mt-0.5" />
              <p className="text-xs text-muted-foreground">{ride?.car_details}</p>
            </div>
          )}
          {ride?.notes && (
            <div className="flex items-start gap-2">
              <Icon name="MessageSquare" size={14} className="text-muted-foreground mt-0.5" />
              <p className="text-xs text-muted-foreground">{ride?.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Passengers Section - Show only for driver or joined users */}
      {(isDriver || isPassenger) && ride?.passengers && ride?.passengers?.length > 0 && (
        <div className="border-t border-gray-200 pt-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Users" size={16} className="text-muted-foreground" />
            <h4 className="text-sm font-semibold text-foreground">Yolcular ({ride?.passengers?.length})</h4>
          </div>
          <div className="space-y-2">
            {ride?.passengers?.map((passenger) => (
              <div key={passenger?.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white font-semibold text-xs overflow-hidden">
                    {passenger?.passenger_profile?.avatar_url ? (
                      <img
                        src={passenger?.passenger_profile?.avatar_url}
                        alt={passenger?.passenger_profile?.full_name || 'Yolcu'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span>{passenger?.passenger_profile?.full_name?.charAt(0)?.toUpperCase() || 'Y'}</span>
                    )}
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {passenger?.passenger_profile?.full_name || 'İsimsiz Yolcu'}
                  </span>
                </div>
                {/* Message button for driver to contact passengers */}
                {isDriver && passenger?.passenger_id !== currentUserId && (
                  <button
                    onClick={() => handleStartChat(passenger?.passenger_id)}
                    className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    title="Yolcuya Mesaj Gönder"
                  >
                    <Icon name="MessageCircle" size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      {currentUserId && (
        <div>
          {isDriver ? (
            <button
              onClick={handleCancelRide}
              disabled={isLoading}
              className="w-full py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>İptal Ediliyor...</span>
                </>
              ) : (
                <>
                  <Icon name="X" size={16} />
                  <span>İlanı İptal Et</span>
                </>
              )}
            </button>
          ) : isPassenger ? (
            <button
              onClick={handleLeaveRide}
              disabled={isLoading}
              className="w-full py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Ayrılınıyor...</span>
                </>
              ) : (
                <>
                  <Icon name="LogOut" size={16} />
                  <span>Araçtan Ayrıl</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleJoinRide}
              disabled={isLoading || isFull}
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Katılınıyor...</span>
                </>
              ) : isFull ? (
                <>
                  <Icon name="Lock" size={16} />
                  <span>Dolu</span>
                </>
              ) : (
                <>
                  <Icon name="UserPlus" size={16} />
                  <span>Katıl</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {!currentUserId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
          <p className="text-xs text-blue-700">Araca katılmak için giriş yapmalısınız</p>
        </div>
      )}
    </div>
  );
};

export default RideCard;